import { useState } from "react";

import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowForwardIcon,
  ChatIcon,
  CloseSVGIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useSendSmsToFreelancerMutation } from "@js/apps/freelancer/api";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import type { FreelancerForNodeStaff } from "@js/types/freelancer";

import styles from "./styles.module.scss";

const SMS_SEGMENT_SIZE = 160;

type SendSmsModalProps = {
  freelancer: FreelancerForNodeStaff;
  formattedPhone: string;
};

export const SendSmsModal = ({
  freelancer,
  formattedPhone,
}: SendSmsModalProps) => {
  const [message, setMessage] = useState("");
  const [sendSms, { isLoading }] = useSendSmsToFreelancerMutation();

  const segmentCount = Math.max(
    1,
    Math.ceil(message.length / SMS_SEGMENT_SIZE),
  );
  const segmentMax = segmentCount * SMS_SEGMENT_SIZE;

  const handleSend = async () => {
    if (!message.trim()) {
      return;
    }
    try {
      await sendSms({ freelancerId: freelancer.id, message }).unwrap();
      Snackbar.success("SMS sent successfully.");
      ModalInstance.close();
    } catch (error: unknown) {
      const mappedError = error as
        { data?: Record<string, string> } | undefined;
      const errorData = mappedError?.data;

      const errorKeys = ["phone_number", "message", "_error"];
      const errorMessageKey = errorKeys.find((key) => !!errorData?.[key]);
      const errorMessage =
        errorMessageKey && errorData?.[errorMessageKey]
          ? errorData?.[errorMessageKey]
          : "Failed to send SMS. Please try again.";

      Snackbar.error(errorMessage);
    }
  };

  return (
    <Stack sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" sx={{ mb: 2.5, gap: 2, alignItems: "start" }}>
        <Box
          sx={{
            mt: "2px",
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: "var(--grey-4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ChatIcon sx={{ color: "var(--grey-1)" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="h2"
            variant="paragraph"
            size="large"
            fontWeight={500}
            sx={{ lineHeight: "1.4 !important" }}
          >
            New text message
          </Typography>
          <Typography
            component="p"
            variant="paragraph"
            color="secondary"
            size="small"
          >
            Sent as SMS — separate from the inbox thread
          </Typography>
        </Box>
        <IconButton
          aria-label="close modal"
          variant="transparent"
          onClick={() => ModalInstance.close()}
          sx={{
            borderRadius: "50% !important",
            backgroundColor: "var(--grey-4)",
            flexShrink: 0,
          }}
        >
          <CloseSVGIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          backgroundColor: "var(--soft-violet)",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar src={freelancer.user} size="md" />
        <Box>
          <Typography component="p" fontWeight={500}>
            {freelancer.user.public_name}
          </Typography>
          <Typography
            component="p"
            variant="paragraph"
            size="small"
            color="secondary"
          >
            {formattedPhone}
          </Typography>
        </Box>
      </Box>

      <TextField
        multiline
        id="sms-message"
        rows={6}
        fullWidth
        placeholder="Type a text message..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ mb: 1, textarea: { p: "0 !important" } }}
        slotProps={{ htmlInput: { maxLength: 1600 } }}
      />

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          color="secondary"
        >
          {segmentCount} SMS segment{segmentCount !== 1 ? "s" : ""}
        </Typography>
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          color="secondary"
        >
          {message.length} / {segmentMax}
        </Typography>
      </Stack>

      <Box
        sx={{
          backgroundColor: "var(--grey-4)",
          borderRadius: "8px",
          p: 2,
          mb: 3,
        }}
      >
        <Typography
          component="p"
          variant="paragraph"
          size="small"
          color="secondary"
        >
          Standard carrier message &amp; data rates may apply. Replies are not
          delivered to your Braintrust inbox.
        </Typography>
      </Box>

      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1 }}>
        <Button
          variant="secondary"
          shape="squared"
          onClick={() => ModalInstance.close()}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="positive"
          shape="squared"
          onClick={handleSend}
          disabled={!message.trim() || isLoading}
          endIcon={<ArrowForwardIcon />}
        >
          Send SMS
        </Button>
      </Stack>
    </Stack>
  );
};

export const openSendSmsModal = (props: SendSmsModalProps) => {
  ModalInstance.open({
    children: <SendSmsModal {...props} />,
    padding: false,
    closeButton: false,
    className: styles.modal,
  });
};
